<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      attachments: [...this.edattachments],
      attachmentLoading: false,
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    triggerAttach() {
      this.$refs.attachInput?.click();
    },
    async onAttachFile(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;

      this.attachmentLoading = true;
      try {
        const attachment = await this.$store.dispatch('attachCannedMedia', {
          id: this.id,
          file,
        });
        this.attachments.push(attachment);
        useAlert(this.$t('CANNED_MGMT.ATTACH.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.ATTACH.API.ERROR_MESSAGE'));
      } finally {
        this.attachmentLoading = false;
      }
    },
    async deleteAttachment(attachment) {
      try {
        await this.$store.dispatch('deleteCannedMedia', {
          id: this.id,
          attachmentId: attachment.id,
        });
        this.attachments = this.attachments.filter(
          item => item.id !== attachment.id
        );
        useAlert(this.$t('CANNED_MGMT.ATTACH.API.DELETE_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.ATTACH.API.DELETE_ERROR_MESSAGE'));
      }
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full mt-2">
          <label>{{ $t('CANNED_MGMT.ATTACH.LABEL') }}</label>
          <div v-if="attachments.length" class="flex flex-wrap gap-2 mt-1">
            <div
              v-for="attachment in attachments"
              :key="attachment.id"
              class="relative flex items-center gap-2 max-w-56 py-1.5 pl-2 pr-8 rounded-md border border-slate-100 dark:border-slate-700 bg-slate-25 dark:bg-slate-800"
            >
              <img
                v-if="attachment.file_type === 'image'"
                :src="attachment.thumb_url"
                :alt="attachment.filename"
                class="object-cover rounded size-8"
              />
              <fluent-icon
                v-else
                icon="document"
                class="text-slate-600 dark:text-slate-200"
              />
              <span
                class="text-sm truncate text-slate-700 dark:text-slate-100"
                :title="attachment.filename"
              >
                {{ attachment.filename }}
              </span>
              <woot-button
                v-tooltip.top="$t('CANNED_MGMT.ATTACH.REMOVE')"
                variant="clear"
                color-scheme="alert"
                size="tiny"
                icon="dismiss-circle"
                class="absolute right-0.5 top-0.5"
                @click="deleteAttachment(attachment)"
              />
            </div>
          </div>
          <NextButton
            faded
            slate
            sm
            icon="i-lucide-paperclip"
            type="button"
            class="mt-2"
            :label="$t('CANNED_MGMT.ATTACH.BUTTON_TEXT')"
            :is-loading="attachmentLoading"
            @click.prevent="triggerAttach"
          />
          <input
            ref="attachInput"
            type="file"
            class="hidden"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            @change="onAttachFile"
          />
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
